import React from "react"
import MyCard from "@common-utils/MyCard"
import MyTooltip from "@common-utils/MyTooltip"
import { Form, Formik, FormikHelpers } from "formik"
import { CONST_API_UTILS_GET_ACTIVE_PERSONNELS_ENDPOINT } from "../../../../endpoints"
import { useAuth } from "../../../../AppContext"
import { CONST_ANNONCE_ETATS_OPTIONS_FOR_SELECT2, CONST_VIVIER_STATUS_DISPONIBLE } from "@common-constants/*"
import { isEqual } from "lodash"

// 🔹 New Field Components
import MyInputFormField from "@common-components/fields/MyInputFormField"
import MySelectGetAllDataFromServerFormField from "@common-components/fields/MySelectGetAllDataFromServerFormField"
import MySelectFormField from "@common-components/fields/MySelectFormField"
import { useAnnoncesListingContext } from "./AnnoncesListing"
import { IGetAnnoncesRequestModel } from "./core/_models"

const ViviersListingFilters = () => {
     const context = useAnnoncesListingContext()
     const { currentUser } = useAuth()

     const initialValues: IGetAnnoncesRequestModel = {
          ...context.filters,
     }

     function handleSubmit(values: IGetAnnoncesRequestModel, helpers: FormikHelpers<IGetAnnoncesRequestModel>) {
          const newFilters = { ...context.filters, ...values, page: 1 }

          helpers.setSubmitting(false)

          context.setFilters(newFilters)

          context.setAreFiltersVisible(false)
     }

     function handleReset() {
          const defaultFilters = {
               page: 1,
               pageLength: 20,
               responsables: currentUser?.teamRecrutement?.map(item => item.id as number) || [],
               keyword: "",
               statuses: [CONST_VIVIER_STATUS_DISPONIBLE],
          }

          // 🔹 FIX: If already at default, just reset form UI and stop loaders
          if (isEqual(context.filters, defaultFilters)) {
               if (context.filtersFormikRef.current) {
                    context.filtersFormikRef.current.resetForm({ values: defaultFilters })
                    context.filtersFormikRef.current.setSubmitting(false)
               }
               if (context.simpleSearchBarFormikRef.current) {
                    context.simpleSearchBarFormikRef.current.resetForm({ values: { keyword: "" } })
                    context.simpleSearchBarFormikRef.current.setSubmitting(false)
               }
               if (window.innerWidth < 992) {
                    context.setAreFiltersVisible(false)
               }
               return
          }

          // 2. Update Global State (Will trigger fetch)
          context.setFilters(defaultFilters)
          if (window.innerWidth < 992) {
               context.setAreFiltersVisible(false)
          }

          // 3. Reset Local Forms & Set Loading
          if (context.filtersFormikRef.current) {
               context.filtersFormikRef.current.resetForm({ values: defaultFilters })
               context.filtersFormikRef.current.setSubmitting(true)
          }

          if (context.simpleSearchBarFormikRef.current) {
               context.simpleSearchBarFormikRef.current.resetForm({ values: { keyword: "" } })
          }
     }

     function handleClickHideFilters() {
          context.setAreFiltersVisible(prev => !prev)
          // Re-open preview if it was hidden when filters appeared
          context.setItemInPreview(prev => {
               if (prev) return { ...prev, isPreviewVisible: true }
               return null
          })
     }

     const layoutProps = {
          classNameLayout: "mb-5",
          classNameLabel: "col-12 col-form-label fw-bold fs-6",
          classNameField: "col-12",
     }

     return (
          <MyCard>
               <MyCard.Header
                    isCollapseIconShown={false}
                    rightSideContent={
                         <MyTooltip title={"Cacher les filtres"}>
                              <button type="button" className={"btn btn-sm btn-outline btn-outline-secondary"} onClick={handleClickHideFilters}>
                                   <i className={"fas fa-eye-slash fs-4 text-dark"} />
                              </button>
                         </MyTooltip>
                    }
               >
                    Filtres
               </MyCard.Header>

               <MyCard.Body>
                    <Formik initialValues={initialValues} onSubmit={handleSubmit} innerRef={context.filtersFormikRef} enableReinitialize={true}>
                         {formikProps => {
                              return (
                                   <Form noValidate autoComplete="off">
                                        <MyInputFormField
                                             label="Mot clé"
                                             name="keyword"
                                             placeholder="Rechercher ..."
                                             formikProps={formikProps}
                                             onChangeCallback={val => {
                                                  if (context.simpleSearchBarFormikRef.current) {
                                                       context.simpleSearchBarFormikRef.current.setFieldValue("keyword", val).then()
                                                  }
                                             }}
                                             {...layoutProps}
                                        />

                                        <div className="separator separator-dotted mt-4 mb-8"></div>

                                        <MySelectGetAllDataFromServerFormField
                                             label="Responsables"
                                             name="responsables"
                                             placeholder="Sélectionner des responsables"
                                             endpoint={CONST_API_UTILS_GET_ACTIVE_PERSONNELS_ENDPOINT}
                                             method="GET"
                                             defaultOptions={
                                                  currentUser?.teamRecrutement && currentUser.teamRecrutement.length > 0
                                                       ? currentUser.teamRecrutement.map(item => ({
                                                              value: item.id as number,
                                                              label: item.prenomNom as string,
                                                         }))
                                                       : []
                                             }
                                             isMulti
                                             formikProps={formikProps}
                                             {...layoutProps}
                                        />

                                        <MySelectFormField
                                             label="Etat"
                                             name="etat"
                                             placeholder="Etat"
                                             options={CONST_ANNONCE_ETATS_OPTIONS_FOR_SELECT2}
                                             formikProps={formikProps}
                                             {...layoutProps}
                                        />

                                        <div className={"text-end mt-4"}>
                                             <button
                                                  className={"btn btn-sm btn-outline btn-outline-primary d-inline-flex align-items-center me-2"}
                                                  type={"button"}
                                                  disabled={formikProps.isSubmitting}
                                                  onClick={() => handleReset()}
                                             >
                                                  <span className={"d-none d-lg-block"}>Réinitialiser</span> <i className={"fas fa-sync ms-lg-1"} />
                                             </button>
                                             <button className={"btn btn-sm btn-primary d-inline-flex align-items-center"} type={"submit"} disabled={formikProps.isSubmitting}>
                                                  <span className={"d-none d-lg-block"}>Filtrer </span>
                                                  {!formikProps.isSubmitting ? (
                                                       <i className={"fas fa-filter ms-md-1"} />
                                                  ) : (
                                                       <span className="spinner-border spinner-border-sm align-middle ms-lg-1" />
                                                  )}
                                             </button>
                                        </div>
                                   </Form>
                              )
                         }}
                    </Formik>
               </MyCard.Body>
          </MyCard>
     )
}

export default ViviersListingFilters
